import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getHostels, getHostelAdmissions, getHostelAdmissionOptions,
  getInstituteAttendanceRoster, markInstituteAttendance, bulkMarkInstituteAttendance, getInstituteAttendanceHistory,
  getMealsHistory, getAttendanceHistory, getHostelPayments, getInvoices, getInvoice,
  getBillingAdjustments, addBillingAdjustment, deleteBillingAdjustment, getResidentHistoryByCnic,
} from '../api';
import { FiHome, FiCheck, FiX, FiUsers, FiClock, FiAlertTriangle, FiDownload, FiUser } from 'react-icons/fi';
import { GROUP_OPTIONS, groupRows } from '../utils/grouping';
import ResidentProfileModal from './ResidentProfileModal';

const todayStr = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };

function downloadCsv(filename, rows) {
  const esc = (v) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const csv = rows.map(r => r.map(esc).join(',')).join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function InstituteAttendance() {
  const [searchParams] = useSearchParams();
  const urlHostelId = searchParams.get('hostel_id');

  const [hostels, setHostels] = useState([]);
  const [hostelId, setHostelId] = useState('');
  const [date, setDate] = useState(todayStr());
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [groupBy, setGroupBy] = useState('');
  const [campuses, setCampuses] = useState([]);
  const [courses, setCourses] = useState([]);

  const [admissions, setAdmissions] = useState([]);
  const [historyAdmissionId, setHistoryAdmissionId] = useState('');
  const [historyStart, setHistoryStart] = useState(firstOfMonth());
  const [historyEnd, setHistoryEnd] = useState(todayStr());
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [mismatchOnly, setMismatchOnly] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [profileRecord, setProfileRecord] = useState(null);

  useEffect(() => {
    getHostels().then(res => {
      setHostels(res.data);
      if (urlHostelId) setHostelId(urlHostelId);
      else if (res.data.length && !hostelId) setHostelId(res.data[0].id);
    }).catch(() => setError('Failed to load hostels'));
    getHostelAdmissionOptions().then(res => {
      setCampuses(res.data.campuses || []);
      setCourses(res.data.courses || []);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hostelId) return;
    getHostelAdmissions({ hostel_id: hostelId, status: 'active' }).then(res => {
      setAdmissions(Array.isArray(res.data) ? res.data : []);
    }).catch(() => {});
  }, [hostelId]);

  useEffect(() => {
    if (!historyAdmissionId) return;
    const adm = admissions.find(a => String(a.id) === String(historyAdmissionId));
    if (adm?.date_of_admission) setHistoryStart(adm.date_of_admission.slice(0, 10));
  }, [historyAdmissionId, admissions]);

  const admissionsById = useMemo(() => Object.fromEntries(admissions.map(a => [a.id, a])), [admissions]);
  const campusNameById = useMemo(() => Object.fromEntries(campuses.map(c => [c.id, c.name])), [campuses]);
  const courseNameById = useMemo(() => Object.fromEntries(courses.map(c => [c.id, c.name])), [courses]);

  const load = useCallback(async () => {
    if (!hostelId) return;
    setLoading(true);
    setError('');
    try {
      const res = await getInstituteAttendanceRoster({ hostel_id: hostelId, date });
      setRoster(res.data.roster);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load roster');
    }
    setLoading(false);
  }, [hostelId, date]);

  useEffect(() => { load(); }, [load]);

  const loadHistory = useCallback(async () => {
    if (!hostelId) return;
    setHistoryLoading(true);
    try {
      const params = { hostel_id: hostelId, start: historyStart, end: historyEnd };
      if (historyAdmissionId) params.admission_id = historyAdmissionId;
      const res = await getInstituteAttendanceHistory(params);
      setHistory(res.data);
    } catch (e) { /* non-fatal */ }
    setHistoryLoading(false);
  }, [hostelId, historyAdmissionId, historyStart, historyEnd]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const setStatus = async (admissionId, status) => {
    setRoster(prev => prev.map(r => r.admission_id === admissionId ? { ...r, institute_status: status, mismatch: !!(r.hostel_status && r.hostel_status !== status) } : r));
    try {
      await markInstituteAttendance({ admission_id: admissionId, attendance_date: date, status });
      loadHistory();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save');
      load();
    }
  };

  const markAllPresent = async () => {
    setSaving(true);
    try {
      const entries = roster.map(r => ({ admission_id: r.admission_id, status: 'present' }));
      await bulkMarkInstituteAttendance({ hostel_id: hostelId, attendance_date: date, entries });
      load(); loadHistory();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to bulk mark');
    }
    setSaving(false);
  };

  // Combined export: institute attendance + hostel attendance (already joined
  // server-side as hostel_status) + meals, with a Match column — covers
  // "regular" and "meal" attendance together, exactly as requested.
  const exportCombinedCsv = async () => {
    setExporting(true);
    try {
      const mealParams = { hostel_id: hostelId, start: historyStart, end: historyEnd };
      if (historyAdmissionId) mealParams.admission_id = historyAdmissionId;
      const mealRes = await getMealsHistory(mealParams);
      const mealByKey = new Map(mealRes.data.map(m => [`${m.admission_id}_${m.meal_date.slice(0, 10)}`, m]));

      const rows = [['Date', 'Resident', 'Institute Status', 'Hostel Status', 'Match', 'Breakfast', 'Lunch', 'Dinner']];
      history.forEach(h => {
        const dateKey = h.attendance_date.slice(0, 10);
        const meal = mealByKey.get(`${h.admission_id}_${dateKey}`);
        rows.push([
          new Date(h.attendance_date).toLocaleDateString('en-PK'),
          h.full_name,
          h.status,
          h.hostel_status || 'not marked',
          h.hostel_status ? (h.mismatch ? 'MISMATCH' : 'Match') : 'n/a',
          meal?.breakfast ? 'Yes' : 'No',
          meal?.lunch ? 'Yes' : 'No',
          meal?.dinner ? 'Yes' : 'No',
        ]);
      });
      downloadCsv(`institute-vs-hostel-attendance-${hostels.find(h => String(h.id) === String(hostelId))?.name || hostelId}-${historyStart}_to_${historyEnd}.csv`, rows);
    } catch (e) {
      setError('Failed to export');
    }
    setExporting(false);
  };

  const viewProfile = (admissionId, fallbackName) => {
    setProfileRecord(admissionsById[admissionId] || { id: admissionId, full_name: fallbackName });
  };

  const presentCount = roster.filter(r => r.institute_status === 'present').length;
  const absentCount = roster.filter(r => r.institute_status === 'absent').length;
  const mismatchCount = roster.filter(r => r.mismatch).length;

  const rosterGroups = groupRows(roster, groupBy, admissionsById, campusNameById, courseNameById);
  const displayedHistory = mismatchOnly ? history.filter(h => h.mismatch) : history;
  const historyGroups = groupRows(displayedHistory, groupBy, admissionsById, campusNameById, courseNameById);

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2 className="card-title"><FiHome /> Institute Attendance</h2>
          <p style={{ color: 'var(--text-muted)', margin: '4px 0 0' }}>
            Track whether residents actually came to the institute, cross-checked against their hostel attendance for the same day
          </p>
        </div>
      </div>

      <div className="form-row" style={{ marginBottom: '18px' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Hostel</label>
          <select className="form-control" value={hostelId} onChange={e => setHostelId(e.target.value)}>
            {hostels.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Date</label>
          <input type="date" className="form-control" value={date} max={todayStr()} onChange={e => setDate(e.target.value)} />
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Organize By</label>
          <select className="form-control" value={groupBy} onChange={e => setGroupBy(e.target.value)}>
            {GROUP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <>
          <div className="stats-grid" style={{ marginBottom: '18px' }}>
            <div className="stat-card stat-purple">
              <div className="stat-icon"><FiUsers /></div>
              <div className="stat-content"><div className="stat-value">{roster.length}</div><div className="stat-label">Total Residents</div></div>
            </div>
            <div className="stat-card stat-green">
              <div className="stat-icon"><FiCheck /></div>
              <div className="stat-content"><div className="stat-value">{presentCount}</div><div className="stat-label">Present at Institute</div></div>
            </div>
            <div className="stat-card stat-orange">
              <div className="stat-icon"><FiX /></div>
              <div className="stat-content"><div className="stat-value">{absentCount}</div><div className="stat-label">Absent from Institute</div></div>
            </div>
            <div className="stat-card stat-danger">
              <div className="stat-icon"><FiAlertTriangle /></div>
              <div className="stat-content"><div className="stat-value">{mismatchCount}</div><div className="stat-label">Mismatch vs Hostel</div></div>
            </div>
          </div>

          <div className="action-buttons" style={{ marginBottom: '14px' }}>
            <button className="btn btn-dark btn-small" onClick={markAllPresent} disabled={saving || !roster.length}>
              {saving ? 'Saving...' : 'Mark All Present'}
            </button>
          </div>

          {rosterGroups.map((group, gi) => (
            <div key={gi} style={{ marginBottom: '18px' }}>
              {group.label && <h4 style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '0 0 8px' }}>{group.label} ({group.rows.length})</h4>}
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr><th>#</th><th>Resident</th><th>Institute</th><th>Hostel (same day)</th><th>Flag</th><th>Mark</th></tr>
                  </thead>
                  <tbody>
                    {group.rows.length === 0 ? (
                      <tr><td colSpan="6" style={{ textAlign: 'center', padding: '30px', color: 'var(--text-muted)' }}>No active residents in this hostel.</td></tr>
                    ) : group.rows.map((r, i) => (
                      <tr key={r.admission_id} style={r.mismatch ? { background: 'rgba(239,68,68,0.06)' } : undefined}>
                        <td>{i + 1}</td>
                        <td>
                          <strong>{r.full_name}</strong>{' '}
                          <button className="btn btn-small btn-secondary" title="View resident profile" onClick={() => viewProfile(r.admission_id, r.full_name)}><FiUser size={12} /></button>
                        </td>
                        <td>
                          {r.institute_status === 'present' && <span className="badge badge-success">Present</span>}
                          {r.institute_status === 'absent' && <span className="badge badge-danger">Absent</span>}
                          {!r.institute_status && <span className="badge badge-muted">Not marked</span>}
                        </td>
                        <td>
                          {r.hostel_status === 'present' && <span className="badge badge-success">Present</span>}
                          {r.hostel_status === 'absent' && <span className="badge badge-danger">Absent</span>}
                          {!r.hostel_status && <span className="badge badge-muted">Not marked</span>}
                        </td>
                        <td>{r.mismatch && <span className="badge badge-danger"><FiAlertTriangle size={11} /> Mismatch</span>}</td>
                        <td>
                          <div className="action-buttons">
                            <button className={`btn btn-small ${r.institute_status === 'present' ? 'btn-success' : 'btn-secondary'}`} onClick={() => setStatus(r.admission_id, 'present')}>Present</button>
                            <button className={`btn btn-small ${r.institute_status === 'absent' ? 'btn-danger' : 'btn-secondary'}`} onClick={() => setStatus(r.admission_id, 'absent')}>Absent</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </>
      )}

      {/* History */}
      <div style={{ marginTop: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
          <h3 style={{ fontSize: '15px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}><FiClock /> History</h3>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', cursor: 'pointer' }}>
              <input type="checkbox" checked={mismatchOnly} onChange={e => setMismatchOnly(e.target.checked)} />
              Mismatches only
            </label>
            <button className="btn btn-secondary btn-small" onClick={exportCombinedCsv} disabled={!history.length || exporting}>
              <FiDownload /> {exporting ? 'Exporting...' : 'Export CSV (regular + meal)'}
            </button>
          </div>
        </div>
        <div className="form-row" style={{ marginBottom: '14px' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Resident</label>
            <select className="form-control" value={historyAdmissionId} onChange={e => setHistoryAdmissionId(e.target.value)}>
              <option value="">All residents</option>
              {admissions.map(a => <option key={a.id} value={a.id}>{a.full_name}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>From{historyAdmissionId ? ' (defaults to enrollment date)' : ''}</label>
            <input type="date" className="form-control" value={historyStart} onChange={e => setHistoryStart(e.target.value)} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>To</label>
            <input type="date" className="form-control" value={historyEnd} max={todayStr()} onChange={e => setHistoryEnd(e.target.value)} />
          </div>
        </div>

        {historyLoading ? (
          <div className="loading"><div className="spinner"></div></div>
        ) : historyGroups.map((group, gi) => (
          <div key={gi} style={{ marginBottom: '18px' }}>
            {group.label && <h4 style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '0 0 8px' }}>{group.label} ({group.rows.length})</h4>}
            <div className="table-container">
              <table className="data-table">
                <thead><tr><th>Date</th><th>Resident</th><th>Institute</th><th>Hostel</th><th>Flag</th></tr></thead>
                <tbody>
                  {group.rows.length === 0 ? (
                    <tr><td colSpan="5" style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)' }}>No records in this range.</td></tr>
                  ) : group.rows.map(row => (
                    <tr key={row.id} style={row.mismatch ? { background: 'rgba(239,68,68,0.06)' } : undefined}>
                      <td>{new Date(row.attendance_date).toLocaleDateString('en-PK')}</td>
                      <td>
                        {row.full_name}{' '}
                        <button className="btn btn-small btn-secondary" title="View resident profile" onClick={() => viewProfile(row.admission_id, row.full_name)}><FiUser size={12} /></button>
                      </td>
                      <td>{row.status === 'present' ? <span className="badge badge-success">Present</span> : <span className="badge badge-danger">Absent</span>}</td>
                      <td>{row.hostel_status ? (row.hostel_status === 'present' ? <span className="badge badge-success">Present</span> : <span className="badge badge-danger">Absent</span>) : <span className="badge badge-muted">Not marked</span>}</td>
                      <td>{row.mismatch && <span className="badge badge-danger"><FiAlertTriangle size={11} /> Mismatch</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      {profileRecord && (
        <ResidentProfileModal
          admission={profileRecord}
          campuses={campuses}
          courses={courses}
          onClose={() => setProfileRecord(null)}
          fetchAttendance={(id) => getAttendanceHistory({ admission_id: id })}
          fetchMeals={(id) => getMealsHistory({ admission_id: id })}
          fetchPayments={(id) => getHostelPayments({ admission_id: id })}
          fetchInvoices={(id) => getInvoices({ admission_id: id })}
          fetchInvoiceDetail={(id) => getInvoice(id)}
          fetchAdjustments={(id) => getBillingAdjustments({ admission_id: id })}
          fetchResidentHistory={(cnic) => getResidentHistoryByCnic(cnic)}
          canManageAdjustments
          onAddAdjustment={addBillingAdjustment}
          onDeleteAdjustment={deleteBillingAdjustment}
        />
      )}
    </div>
  );
}
